
import React, { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

const STATUS_TABS = [
  { label: "All", value: "ALL" },
  { label: "Pending", value: "PENDING" },
  { label: "Partial", value: "PARTIAL" },
  { label: "Completed", value: "COMPLETED" },
];

const EMPTY_FORM = {
  pdc_date: "",
  poh_date: "",
  gang_no: "",
  remark: "",
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toInputDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getStatus = (row) => {
  const pdc = Boolean(row.pdc_date);
  const poh = Boolean(row.poh_date);

  if (pdc && poh) return "COMPLETED";
  if (pdc || poh) return "PARTIAL";
  return "PENDING";
};

const statusColor = (status) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "PARTIAL":
      return "warning";
    default:
      return "default";
  }
};

const Pdc = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    partial: 0,
    completed: 0,
  });

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCoach, setHistoryCoach] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/pdc", {
        params: {
          page: page + 1,
          limit,
          search,
          status,
        },
      });

      const data = response.data || {};

      setRows(
        Array.isArray(data.rows)
          ? data.rows
          : Array.isArray(data.data)
            ? data.data
            : []
      );

      setTotal(
        Number(data.total ?? data.pagination?.total ?? 0)
      );

      const counts = data.summary || data.counts || data.statusCounts;
      if (counts) {
        setSummary({
          total: Number(counts.total ?? counts.all ?? counts.ALL ?? 0),
          pending: Number(counts.pending ?? counts.PENDING ?? 0),
          partial: Number(counts.partial ?? counts.PARTIAL ?? 0),
          completed: Number(counts.completed ?? counts.COMPLETED ?? 0),
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to load PDC records."
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleTabChange = (_event, newValue) => {
    setStatus(newValue);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm({
      pdc_date: toInputDate(row.pdc_date),
      poh_date: toInputDate(row.poh_date),
      gang_no: row.gang_no || "",
      remark: row.remark || "",
    });
    setError("");
    setSuccess("");
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditOpen(false);
    setSelected(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!selected?.visit_id) {
      setError("Visit ID is missing.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await api.put(`/pdc/${selected.visit_id}`, {
        pdc_date: form.pdc_date || null,
        poh_date: form.poh_date || null,
        gang_no: form.gang_no.trim(),
        remark: form.remark.trim(),
      });

      setEditOpen(false);
      setSelected(null);
      setSuccess("PDC details saved successfully.");
      await fetchRows();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to save PDC details."
      );
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryCoach(row);
    setHistoryRows([]);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setError("");

    try {
      const response = await api.get(
        `/pdc/${row.visit_id}/history`
      );

      const data = response.data || {};
      setHistoryRows(
        Array.isArray(data.rows)
          ? data.rows
          : Array.isArray(data.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : []
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to load date history."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setHistoryRows([]);
    setHistoryCoach(null);
  };

  const renderStatus = (row) => {
    const value = getStatus(row);

    return (
      <Chip
        size="small"
        label={value}
        color={statusColor(value)}
        variant={value === "PENDING" ? "outlined" : "filled"}
      />
    );
  };

  const renderActions = (row) => (
    <Stack direction="row" spacing={0.5}>
      <Tooltip title="Edit PDC details">
        <IconButton
          size="small"
          color="primary"
          onClick={() => openEdit(row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Date change history">
        <IconButton
          size="small"
          color="secondary"
          onClick={() => openHistory(row)}
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const tabCounts = {
    ALL: summary.total,
    PENDING: summary.pending,
    PARTIAL: summary.partial,
    COMPLETED: summary.completed,
  };

  const summaryCards = [
    { label: "Total Coaches", value: summary.total, color: "text.primary" },
    { label: "Pending", value: summary.pending, color: "text.secondary" },
    { label: "Partial", value: summary.partial, color: "warning.main" },
    { label: "Completed", value: summary.completed, color: "success.main" },
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: "100%", minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Coach PDC
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage PDC and POH dates for coaches in the yard
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRows}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess("")}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {summaryCards.map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ p: "14px !important" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={item.color}
                  sx={{ mt: 0.5 }}
                >
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid> */}

      <Paper variant="outlined" sx={{ mb: 2 }}>
       
<Tabs
  value={status}
  onChange={handleTabChange}
  variant={isMobile ? "scrollable" : "standard"}
  scrollButtons={isMobile ? false : "auto"}
  allowScrollButtonsMobile
  sx={{
    borderBottom: 1,
    borderColor: "divider",
    minHeight: { xs: 44, sm: 48 },

    "& .MuiTabs-flexContainer": {
      gap: { xs: 0.5, sm: 1 },
    },

    "& .MuiTab-root": {
      minWidth: { xs: 90, sm: 145 },
      minHeight: { xs: 44, sm: 48 },
      px: { xs: 1.5, sm: 2 },
      py: 1,
      textTransform: "none",
      fontWeight: 600,
      fontSize: { xs: "0.78rem", sm: "0.9rem" },
      whiteSpace: "nowrap",
    },

    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
    },

    "& .MuiTabs-scroller": {
      overflowX: "auto !important",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  }}
>
  {STATUS_TABS.map((tab) => (
    <Tab
      key={tab.value}
      value={tab.value}
      label={
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{tab.label}</span>
          <Chip
            size="small"
            label={tabCounts[tab.value] ?? 0}
            color={
              tab.value === "COMPLETED"
                ? "success"
                : tab.value === "PARTIAL"
                  ? "warning"
                  : "default"
            }
            variant={tab.value === "ALL" ? "outlined" : "filled"}
            sx={{
              height: 24,
              minWidth: 30,
              fontWeight: 700,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Stack>
      }
    />
  ))}
</Tabs>

        <Box sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search coach no., type, railway, base..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center" }}
        >
          <Typography color="text.secondary">
            No PDC records found.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Card key={row.visit_id} variant="outlined">
              <CardContent sx={{ p: 1.75 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700}>
                      {row.coach_no || row.coach_number || "-"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {row.coach_type || row.coach_type_name || "-"}
                    </Typography>
                  </Box>
                  {renderStatus(row)}
                </Stack>

                <Divider sx={{ my: 1.25 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Visit ID
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {row.visit_id}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Railway / Base
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {[row.railway_code, row.base_code]
                        .filter(Boolean)
                        .join(" / ") || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      PDC Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(row.pdc_date)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      POH Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(row.poh_date)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Gang No.
                    </Typography>
                    <Typography variant="body2">
                      {row.gang_no || "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ mt: 1 }}
                >
                  {renderActions(row)}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Coach No.",
                    "Visit ID",
                    "Coach Type",
                    "Railway",
                    "Base",
                    "PDC Date",
                    "POH Date",
                    "Gang No.",
                    "Status",
                    "Actions",
                  ].map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        bgcolor: "background.default",
                      }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow hover key={row.visit_id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.coach_no || row.coach_number || "-"}
                    </TableCell>
                    <TableCell>{row.visit_id}</TableCell>
                    <TableCell>
                      {row.coach_type || row.coach_type_name || "-"}
                    </TableCell>
                    <TableCell>{row.railway_code || "-"}</TableCell>
                    <TableCell>{row.base_code || "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(row.pdc_date)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(row.poh_date)}
                    </TableCell>
                    <TableCell>{row.gang_no || "-"}</TableCell>
                    <TableCell>{renderStatus(row)}</TableCell>
                    <TableCell>{renderActions(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={limit}
        onRowsPerPageChange={(event) => {
          setLimit(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
        sx={{
          "& .MuiTablePagination-toolbar": {
            px: { xs: 0, sm: 2 },
            flexWrap: "wrap",
          },
        }}
      />

      {/* Edit dialog */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Update PDC Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Coach: {selected?.coach_no || selected?.coach_number || "-"}
              </Typography>
            </Box>

            <IconButton onClick={closeEdit} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="PDC Date"
              name="pdc_date"
              type="date"
              value={form.pdc_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="POH Date"
              name="poh_date"
              type="date"
              value={form.poh_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Gang No."
              name="gang_no"
              value={form.gang_no}
              onChange={handleFormChange}
              fullWidth
              inputProps={{ maxLength: 50 }}
            />

            <TextField
              label="Remark"
              name="remark"
              value={form.remark}
              onChange={handleFormChange}
              fullWidth
              multiline
              minRows={3}
            />

            <Alert severity="info">
              Changes to PDC and POH dates are recorded in date history.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Details"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date history dialog */}
      <Dialog
        open={historyOpen}
        onClose={closeHistory}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Date Change History
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Coach: {historyCoach?.coach_no || historyCoach?.coach_number || "-"}
              </Typography>
            </Box>
            <IconButton onClick={closeHistory}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : historyRows.length === 0 ? (
            <Typography
              color="text.secondary"
              textAlign="center"
              sx={{ py: 4 }}
            >
              No date changes recorded.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Date Field",
                      "Old Date",
                      "New Date",
                      "Changed By",
                      "Changed At",
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {historyRows.map((item, index) => (
                    <TableRow
                      key={item.id || `${item.date_field}-${index}`}
                    >
                      <TableCell>
                        {item.date_field === "PDC_DATE"
                          ? "PDC Date"
                          : item.date_field === "POH_DATE"
                            ? "POH Date"
                            : item.date_field || "-"}
                      </TableCell>
                      <TableCell>{formatDate(item.old_date)}</TableCell>
                      <TableCell>{formatDate(item.new_date)}</TableCell>
                      <TableCell>{item.changedby || item.changed_by || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.changed_at
                          ? new Date(item.changed_at).toLocaleString()
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeHistory}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pdc;